import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { EmptyState } from "@/components/states";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { SERVICE_LABEL, SERVICE_TYPES, type ServiceType } from "@/lib/services";
import {
  ENQUIRY_STATUSES,
  ENQUIRY_STATUS_CLASS,
  ENQUIRY_STATUS_LABEL,
  closeEnquiry,
  listPartnerEnquiries,
  markEnquiryRead,
  replyToEnquiry,
  type Enquiry,
  type EnquiryStatus,
} from "@/lib/enquiries";

export const Route = createFileRoute("/_authenticated/partner/enquiries")({
  head: () => ({
    meta: [
      { title: "Enquiries · Landoo" },
      {
        name: "description",
        content:
          "Read, reply to and close customer enquiries for your Landoo Sale, Rent, Lease and Parking listings.",
      },
    ],
  }),
  component: EnquiriesPage,
});

function EnquiriesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [service, setService] = useState<ServiceType | "all">("all");
  const [status, setStatus] = useState<EnquiryStatus | "all">("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [reply, setReply] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["partner", "enquiries", user?.id],
    queryFn: () => listPartnerEnquiries(user!.id),
    enabled: !!user,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["partner", "enquiries", user?.id] });

  const readMutation = useMutation({ mutationFn: markEnquiryRead, onSuccess: refresh });
  const replyMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => replyToEnquiry(id, text),
    onSuccess: () => {
      toast.success("Reply saved. The customer can see it in their enquiries.");
      setOpenId(null);
      setReply("");
      refresh();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not save the reply."),
  });
  const closeMutation = useMutation({
    mutationFn: closeEnquiry,
    onSuccess: () => {
      toast.success("Enquiry closed.");
      refresh();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not close the enquiry."),
  });

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          (service === "all" || r.service_type === service) &&
          (status === "all" || r.enquiry_status === status),
      ),
    [rows, service, status],
  );

  const countService = (t: ServiceType) => rows.filter((r) => r.service_type === t).length;
  const countStatus = (s: EnquiryStatus) => rows.filter((r) => r.enquiry_status === s).length;

  const openReply = (e: Enquiry) => {
    setOpenId(e.id);
    setReply(e.partner_reply ?? "");
    if (e.enquiry_status === "new") readMutation.mutate(e.id);
  };

  return (
    <DashboardShell title="Enquiries" role="Partner" items={PARTNER_NAV}>
      <div className="flex flex-wrap gap-1.5">
        <Chip
          active={service === "all"}
          onClick={() => setService("all")}
          label={`All services · ${rows.length}`}
        />
        {SERVICE_TYPES.map((t) => (
          <Chip
            key={t}
            active={service === t}
            onClick={() => setService(t)}
            label={`${SERVICE_LABEL[t]} · ${countService(t)}`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Chip
          small
          active={status === "all"}
          onClick={() => setStatus("all")}
          label="All statuses"
        />
        {ENQUIRY_STATUSES.map((s) => (
          <Chip
            key={s}
            small
            active={status === s}
            onClick={() => setStatus(s)}
            label={`${ENQUIRY_STATUS_LABEL[s]} · ${countStatus(s)}`}
          />
        ))}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Customers can only send enquiries. You can mark them read, reply once and close them here.
      </p>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading enquiries…</p>
      ) : visible.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            title="No enquiries here"
            description="Enquiries from customers appear here, grouped by service and status."
          />
        </div>
      ) : (
        <div className="mt-4 grid gap-2">
          {visible.map((e) => (
            <div key={e.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-brand-soft px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-brown-dark">
                    {SERVICE_LABEL[e.service_type]}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${ENQUIRY_STATUS_CLASS[e.enquiry_status]}`}
                  >
                    {ENQUIRY_STATUS_LABEL[e.enquiry_status]}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>

              <div className="mt-2 text-sm font-semibold text-brown-dark">
                {e.listing_title ?? e.listing_kind}
              </div>
              <div className="text-xs text-muted-foreground">
                {e.customer_name ?? "Customer"} · {e.customer_contact ?? "no mobile"}
              </div>
              {e.message && <p className="mt-1.5 text-xs text-brown-dark">{e.message}</p>}

              {e.partner_reply && (
                <div className="mt-2 rounded-xl bg-secondary p-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Your reply
                  </p>
                  <p className="mt-1 text-xs text-brown-dark">{e.partner_reply}</p>
                </div>
              )}

              {openId === e.id ? (
                <div className="mt-3">
                  <Textarea
                    value={reply}
                    onChange={(ev) => setReply(ev.target.value)}
                    rows={3}
                    maxLength={1000}
                    aria-label="Reply to enquiry"
                    placeholder="Write your reply…"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => replyMutation.mutate({ id: e.id, text: reply })}
                      disabled={reply.trim().length < 2 || replyMutation.isPending}
                      className="rounded-xl bg-brand px-4 py-2 text-xs font-bold text-brown-dark transition hover:brightness-95 disabled:opacity-60"
                    >
                      Send reply
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenId(null)}
                      className="rounded-xl border border-border px-4 py-2 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                e.enquiry_status !== "closed" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openReply(e)}
                      className="rounded-xl bg-brand px-4 py-2 text-xs font-bold text-brown-dark transition hover:brightness-95"
                    >
                      {e.partner_reply ? "Edit reply" : "Reply"}
                    </button>
                    <button
                      type="button"
                      onClick={() => closeMutation.mutate(e.id)}
                      disabled={closeMutation.isPending}
                      className="rounded-xl border border-border px-4 py-2 text-xs font-semibold text-brown-dark transition hover:bg-secondary disabled:opacity-60"
                    >
                      Close enquiry
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function Chip({
  active,
  onClick,
  label,
  small,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  small?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full font-semibold transition ${small ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"} ${
        active
          ? "bg-brown text-primary-foreground"
          : "bg-secondary text-brown-dark hover:bg-brand-soft"
      }`}
    >
      {label}
    </button>
  );
}
